import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import GeomanistRegular from "./assets/Geomanist-Regular.woff2";

/**
 * Tally design tokens ("Atelier").
 *
 * The palette is unchanged in spirit - #A97D58 is still the Tally brown - but it
 * is now used as an *accent* (active states, avatar, primary actions) rather than
 * as a large flat fill. Surfaces are warm neutrals biased towards the brand hue so
 * they read as chosen rather than as generic grey.
 *
 * Components should take colours from `theme.tally.*` or from the MUI palette
 * roles below, never from literals, so the whole app can be re-themed from here.
 *
 * Only Geomanist Regular (400) is licensed into the repo, so hierarchy is carried
 * by size, colour and spacing rather than by weight. Bold text falls back to the
 * browser's synthetic bold, exactly as it did before this change.
 */
export const tallyTokens = {
  /** Page background. */
  canvas: "#F7F4EF",
  /** Cards, composer, conversation surface. */
  surface: "#FFFDFA",
  /** Drawer, subtle fills, table header. */
  surface2: "#EFE9E1",
  /** Hover state on top of surface2. */
  surface3: "#E6DED3",
  /** Hairline borders. */
  line: "#E2D9CD",
  /** Stronger borders: inputs, focus rings. */
  lineStrong: "#D2C5B4",
  /** Primary text. */
  ink: "#1A1512",
  /** Secondary text. */
  ink2: "#6B5C4E",
  /** Tertiary text, placeholders, icons at rest. */
  ink3: "#98887A",
  /** The Tally brown. Accent, avatar, primary action. */
  brand: "#A97D58",
  /** Brown dark enough to use as text on light surfaces. */
  brandInk: "#7A5436",
  /** Tinted background for active/selected rows. */
  brandWash: "#F1E7DC",
  /** `surface` with the canvas showing through - used for the landing circle,
   *  so the particle field behind it stays faintly visible. */
  surfaceVeil: "rgba(255, 253, 250, 0.85)",

  good: "#1E7A4F",
  goodWash: "#E3F0E9",
  warn: "#9A6612",
  warnWash: "#F6EDDC",
  crit: "#B3322E",
  critWash: "#F8E6E4",

  radius: {
    sm: 8,
    md: 12,
    lg: 18,
    pill: 999,
  },
  shadow: {
    sm: "0 1px 2px rgba(58,42,28,.06)",
    md: "0 4px 14px rgba(58,42,28,.08), 0 1px 3px rgba(58,42,28,.05)",
    lg: "0 18px 44px rgba(58,42,28,.14), 0 4px 12px rgba(58,42,28,.07)",
  },
};

type TallyTokens = typeof tallyTokens;

declare module "@mui/material/styles" {
  interface Theme {
    tally: TallyTokens;
  }
  interface ThemeOptions {
    tally?: TallyTokens;
  }
}

let baseTheme = createTheme({
  tally: tallyTokens,
  palette: {
    mode: "light",
    background: {
      default: tallyTokens.canvas,
      paper: tallyTokens.surface,
    },
    primary: {
      main: tallyTokens.brand, // Brand accent: avatar, primary actions, active state
      dark: tallyTokens.brandInk,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: tallyTokens.surface, // Conversation panel colour
      contrastText: tallyTokens.ink,
    },
    text: {
      primary: tallyTokens.ink,
      secondary: tallyTokens.ink2,
      disabled: tallyTokens.ink3,
    },
    divider: tallyTokens.line,
    success: { main: tallyTokens.good },
    warning: { main: tallyTokens.warn },
    error: { main: tallyTokens.crit },
    action: {
      hover: "rgba(26, 21, 18, 0.045)",
      selected: tallyTokens.brandWash,
    },
  },
  shape: {
    borderRadius: tallyTokens.radius.md,
  },
  typography: {
    fontFamily: ["Geomanist", "system-ui", "-apple-system", "sans-serif"].join(
      ",",
    ),
    button: {
      textTransform: "none",
    },
  },
});

// Extend the theme with component overrides that use the palette
baseTheme = createTheme(baseTheme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Geomanist';
          src: url(${GeomanistRegular}) format('woff2');
          font-weight: 400;
          font-style: normal;
          ascent-override: 92%;
          descent-override: 30%;
        }
        body {
          background-color: ${tallyTokens.canvas};
          color: ${tallyTokens.ink};
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        ::selection {
          background: ${tallyTokens.brandWash};
        }
      `,
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: tallyTokens.surface,
          border: `1px solid ${tallyTokens.line}`,
          borderRadius: tallyTokens.radius.md,
        },
        cell: {
          color: tallyTokens.ink,
          borderBottom: `1px solid ${tallyTokens.line}`,
          fontVariantNumeric: "tabular-nums",
        },
      },
    },
  },
});

const mainTheme = responsiveFontSizes(baseTheme);
export default mainTheme;
